import { Fragment, useMemo } from "react";

import { APITranscriptToken, Stage } from "../../../types/types";
import { TranscriptToken } from "./transcript-token";

interface TranscriptTokenBatchProps {
  tokenMeta: APITranscriptToken[];
  sliceIndex: number;
  tokenOffsetIndex: number;
  currentTimeMs?: number;
  color: string;
  onClickToken?: (token: APITranscriptToken) => void;
  stage: Stage;
  forceTokens: boolean | null;
  visibleBatches: Record<string, boolean>;
}

/**
 * Only the tokens that are within the viewport or an anchor for a transcript selection are rendered.
 */
export function TranscriptTokenBatch(props: TranscriptTokenBatchProps) {
  const {
    sliceIndex,
    tokenMeta,
    tokenOffsetIndex,
    color,
    currentTimeMs,
    onClickToken,
    stage,
    forceTokens,
    visibleBatches,
  } = props;
  const transcript = useMemo(
    () => tokenMeta.reduce((acc, token) => `${acc}${token.transcript} `, ""),
    [tokenMeta],
  );
  const id = `${sliceIndex}-${tokenOffsetIndex}`;

  const inView = visibleBatches[id];

  const renderTokens = inView || forceTokens;

  return (
    <span className="token-batch" id={id}>
      {renderTokens
        ? tokenMeta.map((token, i) => (
            // there is no guarantee of token uniqueness, so have to use array index in key
            <Fragment key={`${token.startMs}:${token.transcript}:${i}`}>
              <TranscriptToken
                token={token}
                sliceIndex={sliceIndex}
                tokenIndex={tokenOffsetIndex + i}
                color={color}
                current={
                  currentTimeMs !== undefined &&
                  // don't highlight first word if transcript has not been started yet
                  currentTimeMs > 0 &&
                  currentTimeMs >= token.startMs &&
                  (currentTimeMs < token.startMs + token.durationMs ||
                    // if the time is between tokens keep the leading one highlighted
                    // otherwise the active highlight pops in and out
                    (tokenMeta[i + 1] &&
                      currentTimeMs < tokenMeta[i + 1].startMs))
                }
                onClick={onClickToken}
                stage={stage}
              />
              <span data-type="token-space"> </span>
            </Fragment>
          ))
        : transcript}
    </span>
  );
}
